import { useQuery } from "@apollo/client";
import { GET_PENDING_LEADERBOARDS } from "./GetPendingLeaderboards.gql";
import {
    GetPendingLeaderboardsQuery,
    GetPendingLeaderboardsQueryVariables,
} from "./GetPendingLeaderboards.gql.types";
import { useBindToChannelEvent } from "components/providers/Realtime/useBindToChannelEvent";
import { useEventChannel } from "components/routes/events/id/EventProvider";
import { useRefocusEffect } from "hooks/useRefocusEffect";

export type PendingLeaderboardFields = NonNullable<NonNullable<NonNullable<GetPendingLeaderboardsQuery["eventDivision"]>["leaderboards"]>[number]>

export type Progression = { max: number, position: number, customText?: string }[]
type ProgressionEntry = { max?: number | null, to_round?: number, to_heat?: number } | null

export function usePendingLeaderboard({
    eventDivisionId,
    round,
}: {
    eventDivisionId: string;
    round: number;
}) {
    const query = useQuery<
        GetPendingLeaderboardsQuery,
        GetPendingLeaderboardsQueryVariables
    >(GET_PENDING_LEADERBOARDS, {
        variables: {
            eventDivisionId,
            round,
        },
        skip: round === undefined || !eventDivisionId,
        fetchPolicy: "cache-and-network",
    });

    useRefocusEffect(() => query.refetch(), true);

    useBindToChannelEvent<{ event_division_id: string; round: number }>(
        useEventChannel(),
        "leaderboard-updated",
        (msg) => {
            if (
                msg.event_division_id === eventDivisionId &&
                msg.round === round
            ) {
                query.refetch();
            }
        },
    );

    const leaderboard = query.data?.eventDivision?.leaderboards?.[0];
    const leaderboardWithPending =
        query.data?.eventDivision?.leaderboardsWithPending?.[0];

    const manualProgressionRaw = query.data?.eventDivision?.formatDefinition?.manualProgression || {};
    const manualProgression = manualProgressionRaw[round] || false;

    const progressionRaw = query.data?.eventDivision?.formatDefinition?.progression;
    const roundProgression = manualProgression ? [] : progressionRaw?.[round] as ProgressionEntry[] || progressionRaw?.default || [{}];
    const progression = roundProgression.reduce((acc: Progression, progression, i) => {
        if (!progression) return acc;
        console.log("progression", progression, String(progression.max == null));
        if (progression.max == null) {
            // special case for 50% setting - note it's only available as the first
            // progression option so position will always be equal to max
            const halfResultsLength = Math.ceil((leaderboardWithPending?.result.length || 0) / 2);
            if (halfResultsLength) {
                acc.push({ max: halfResultsLength, position: halfResultsLength, customText: "50%" });
            }
        } else {
            acc.push({ max: progression.max, position: (acc[i - 1]?.position || 0) + progression.max });
        }
        return acc;
    }, []);

    return {
        leaderboard,
        leaderboardWithPending,
        progression,
        query,
    };
}
